import { getRouteApi, type RouteIds, useRouter, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { ProductCatalog } from '@client/components/shop/product-catalog.tsx';
import {
  type BreadcrumbItemType,
  BreadcrumbNavigation
} from '@client/components/breadcrumb/breadcrumb-nav.tsx';
import { ShopProductsSidebar } from '@client/components/shop/shop-products-sidebar.tsx';
import { PageContainer } from '@client/components/layout/page-container.tsx';
import { ProductFiltersDrawer } from '@client/components/shop/product-filters-drawer.tsx';
import Marquee from '@ui/marquee.tsx';
import { useTranslation } from 'react-i18next';
import { BreadcrumbSkeleton } from '@client/components/skeleton/breadcrumb-skeleton.tsx';
import { ShopProductsSidebarSkeleton } from '@client/components/skeleton/shop-products-sidebar-skeleton.tsx';
import { ProductCardSkeleton } from '@client/components/skeleton/product-card-skeleton.tsx';
import { GenericPagination } from '@ui/generic-pagination.tsx';
import {
  useApiShopsIdGet,
  useApiShopsShopIdcategoriesGetCollection,
  useApiShopsShopIdproductsGetCollection
} from '@r/api/generated/client.ts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select.tsx';
import type {
  ApiShopsShopIdproductsGetCollectionParams,
  ProductProductReadModel
} from '@r/api/generated/client.schemas.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import {
  getProductCategoriesToFetch,
  isPointsRangeInvalid,
  parseBrandIdsFromSearch,
  parsePointsFromSearch
} from '@r/helpers/product-catalog.ts';

type SearchParams = {
  subcategoryId?: string | number;
  categoryId?: string | number;
  brandIds?: string | number | Array<string | number>;
  pointsMin?: string | number;
  pointsMax?: string | number;
  page?: number;
  perPage?: number;
  priceSort?: 'asc' | 'desc';
  search?: string;
  [key: string]: unknown;
};

const ProductCatalogPage = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const user = useUser().data;
  const workspaces = UseWorkspace(user);
  const search = useSearch({ strict: false }) as SearchParams;

  const PRODUCTS_ROUTE = '/shops/$shopId/products';
  const routeApi = getRouteApi(PRODUCTS_ROUTE as RouteIds<typeof router.routeTree>);
  const { shopId } = routeApi.useParams() as { shopId: string };

  const pageSizeOptions = [10, 25, 50, 100];
  const pageIndex = search.page ? Number(search.page) : 1;
  const pageSize = search.perPage ? Number(search.perPage) : pageSizeOptions[0];
  const priceSort = search.priceSort || 'asc';
  const subcategoryId = search.subcategoryId ? String(search.subcategoryId) : undefined;
  const categoryId = search.categoryId ? String(search.categoryId) : undefined;
  const searchTerm = typeof search.search === 'string' ? search.search.trim() : '';
  const selectedBrandIds = useMemo(
    () => parseBrandIdsFromSearch(search.brandIds),
    [search.brandIds]
  );
  const pointsMinFromUrl = parsePointsFromSearch(search.pointsMin);
  const pointsMaxFromUrl = parsePointsFromSearch(search.pointsMax);
  const isPointsRangeValid = !isPointsRangeInvalid(pointsMinFromUrl, pointsMaxFromUrl);
  const pointsMin = isPointsRangeValid ? pointsMinFromUrl : undefined;
  const pointsMax = isPointsRangeValid ? pointsMaxFromUrl : undefined;

  const isDataAvailable = !!shopId && !!workspaces.workspace?.id && !!user?.id;

  const shopQuery = useApiShopsIdGet(shopId, {
    query: {
      queryKey: ['shop_id', shopId, workspaces.workspace?.id, user?.id],
      enabled: isDataAvailable
    }
  });
  useOrvalQueryErrorToast(shopQuery, t('toast.error.label'));

  const {
    data: shop,
    isSuccess: isShopSuccess,
    isPending: isShopPending,
    isError: isShopError
  } = shopQuery;

  const categoriesQuery = useApiShopsShopIdcategoriesGetCollection(shopId, undefined, {
    query: {
      queryKey: ['categories', shopId, workspaces.workspace?.id, user?.id],
      enabled: isDataAvailable
    }
  });
  useOrvalQueryErrorToast(categoriesQuery, t('toast.error.label'));

  const { data: categories, isSuccess: isProductCategoriesSuccess } = categoriesQuery;

  const isCategoriesDataReady = isDataAvailable && (!categoryId || isProductCategoriesSuccess);

  const productCategoriesList = useMemo(
    () => (isProductCategoriesSuccess && categories ? categories : []),
    [isProductCategoriesSuccess, categories]
  );

  const categoriesToSend = useMemo(
    () => getProductCategoriesToFetch(subcategoryId, categoryId, productCategoriesList),
    [subcategoryId, categoryId, productCategoriesList]
  );
  const productsParams: ApiShopsShopIdproductsGetCollectionParams = {
    page: pageIndex,
    perPage: pageSize
  };

  if (categoriesToSend.length > 0) {
    productsParams['category[]'] = categoriesToSend;
  }
  if (priceSort) {
    productsParams['order[price]'] = priceSort;
  }
  if (searchTerm) {
    productsParams.search = searchTerm;
  }
  if (selectedBrandIds.length > 0) {
    productsParams['brand[]'] = selectedBrandIds.map(String);
  }
  if (pointsMin !== undefined) {
    productsParams['pointUnitPrice[gte]'] = pointsMin;
  }
  if (pointsMax !== undefined) {
    productsParams['pointUnitPrice[lte]'] = pointsMax;
  }

  const productsQuery = useApiShopsShopIdproductsGetCollection(shopId, productsParams, {
    query: {
      queryKey: [
        'products',
        shopId,
        subcategoryId,
        categoryId,
        pageIndex,
        pageSize,
        priceSort,
        searchTerm,
        selectedBrandIds,
        pointsMin,
        pointsMax,
        workspaces.workspace?.id,
        user?.id
      ],
      enabled: isCategoriesDataReady,
      placeholderData: (previousData) => previousData,
      staleTime: 5 * 60 * 1000
    }
  });
  useOrvalQueryErrorToast(productsQuery, t('toast.error.label'));

  const {
    data: products,
    isSuccess: isProductsSuccess,
    isPending: isProductsPending,
    isError: isProductsError
  } = productsQuery;

  const productsList = useMemo(
    (): ProductProductReadModel[] => (isProductsSuccess ? products?.data || [] : []),
    [isProductsSuccess, products?.data]
  );

  const noProductsFound = isProductsSuccess && productsList.length === 0;

  const selectedSubCategory = subcategoryId
    ? productCategoriesList
        .flatMap((category) => category.childs || [])
        .find((subCat) => String(subCat.id) === subcategoryId)
    : undefined;

  const selectedCategory = categoryId
    ? productCategoriesList.find((cat) => String(cat.id) === categoryId)
    : undefined;

  const breadcrumbItems: BreadcrumbItemType[] = [
    { label: t('shops.breadcrumb.title'), to: '/shops' },
    { label: shop?.displayName ?? '', to: `/shops/${shopId}/products` }
  ];

  if (selectedSubCategory) {
    breadcrumbItems.push({
      label:
        selectedSubCategory.label.charAt(0).toUpperCase() +
        selectedSubCategory.label.slice(1).toLowerCase(),
      isCurrentPage: true
    });
  } else if (selectedCategory) {
    breadcrumbItems.push({
      label:
        selectedCategory.label.charAt(0).toUpperCase() +
        selectedCategory.label.slice(1).toLowerCase(),
      isCurrentPage: true
    });
  } else {
    breadcrumbItems[breadcrumbItems.length - 1].isCurrentPage = true;
  }

  const handlePriceSortChange = (newSort?: 'asc' | 'desc') => {
    router.navigate({
      to: PRODUCTS_ROUTE,
      params: { shopId },
      search: (prev) => ({ ...prev, priceSort: newSort }),
      replace: true
    });
  };

  const handlePageChange = (newPage: number) => {
    router.navigate({
      to: PRODUCTS_ROUTE,
      params: { shopId },
      search: (prev) => ({ ...prev, page: newPage }),
      replace: true
    });
  };

  const handlePageSizeChange = (newSize: number) => {
    router.navigate({
      to: PRODUCTS_ROUTE,
      params: { shopId },
      search: (prev) => ({ ...prev, perPage: newSize }),
      replace: true
    });
  };

  return (
    <>
      <PageContainer wide className="pt-10 pb-7 space-y-5">
        {isShopSuccess && isProductsSuccess && <BreadcrumbNavigation items={breadcrumbItems} />}

        {(isShopPending || isShopError || isProductsPending || isProductsError) && (
          <BreadcrumbSkeleton />
        )}

        <div className="space-y-5 md:flex md:space-y-0 md:space-x-5">
          {isShopSuccess && isProductCategoriesSuccess && (
            <ShopProductsSidebar shop={shop} productCategories={productCategoriesList} />
          )}

          {(isShopPending || isShopError) && <ShopProductsSidebarSkeleton />}

          <div className="space-y-5 w-full">
            <div className="hidden justify-end md:flex">
              <div className="flex items-center space-x-2">
                <p className="text-sm font-medium whitespace-nowrap">
                  {t('pagination.sort.price')}
                </p>
                <Select
                  value={priceSort}
                  onValueChange={(value: 'asc' | 'desc') => handlePriceSortChange(value)}
                >
                  <SelectTrigger className="h-8 w-[11rem]">
                    <SelectValue placeholder={t('global.select')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">{t('pagination.sort.price.asc')}</SelectItem>
                    <SelectItem value="desc">{t('pagination.sort.price.desc')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 items-start w-full sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
              {isProductsSuccess &&
                isShopSuccess &&
                productsList.map((product, i) => (
                  <ProductCatalog key={i} shopId={shop?.id} product={product} />
                ))}
              {noProductsFound && !isProductsPending && (
                <div className="flex col-span-full justify-center items-center h-48">
                  <p className="text-xl text-muted-foreground">
                    {(subcategoryId ||
                      categoryId ||
                      selectedBrandIds.length > 0 ||
                      pointsMin !== undefined ||
                      pointsMax !== undefined) &&
                      t('product.catalog.missing.products')}
                  </p>
                </div>
              )}
              {(isProductsPending || isProductsError || isShopPending || isShopError) &&
                Array.from({ length: 6 }).map((_, i) => <ProductCardSkeleton key={i} />)}
            </div>

            {products?.pagination && products.pagination.totalItems >= pageSize && (
              <GenericPagination
                pageSize={pageSize}
                pageSizeOptions={pageSizeOptions}
                pagination={products.pagination}
                priceSort={priceSort}
                onPageSizeChange={handlePageSizeChange}
                onPageChange={handlePageChange}
                onPriceSortChange={handlePriceSortChange}
              />
            )}
          </div>
        </div>
      </PageContainer>

      {isShopSuccess && <Marquee text={shop.displayName} className="hidden lg:block" />}

      {/* Mobile filters drawer */}
      {isShopSuccess && isProductCategoriesSuccess && (
        <ProductFiltersDrawer
          shopId={shopId}
          productCategories={productCategoriesList}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          priceSort={priceSort}
          onPageSizeChange={handlePageSizeChange}
          onPriceSortChange={handlePriceSortChange}
        />
      )}
    </>
  );
};

export { ProductCatalogPage };
